// frontend/src/admin/pages/AdminDashboard.jsx
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { getAdminStats, getCeleryStats } from "../api";

/** Refresh cadence for the dashboard stats (ms). */
const REFRESH_MS = 60_000;
/** Celery widget polls more often — queue depth changes fast. */
const CELERY_REFRESH_MS = 15_000;

/** Format a number as whole-euro EUR (e.g. 1234 → "€1,234"). */
function formatCurrency(value) {
  const n = Number(value) || 0;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-3xl font-bold text-slate-900">{value}</span>
    </div>
  );
}
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

/**
 * CeleryWidget — background-job queue depth + live worker count (P21-G6).
 *
 * Polls GET /api/admin/celery/stats every 15s. Renders nothing until the first
 * successful fetch, and silently hides on error so a broker hiccup never breaks
 * the dashboard.
 */
function CeleryWidget() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await getCeleryStats();
        if (active) setStats(data);
      } catch {
        if (active) setStats(null);
      }
    };
    load();
    const id = setInterval(load, CELERY_REFRESH_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  if (!stats) return null;

  const workersHealthy = (stats.active_workers ?? 0) > 0;
  return (
    <div
      className="mt-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm text-slate-700"
      data-testid="celery-widget"
    >
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${
          workersHealthy ? "bg-emerald-500" : "bg-red-500"
        }`}
        aria-hidden="true"
      />
      <span className="font-medium">Background jobs</span>
      <span className="text-slate-400">·</span>
      <span>
        Queue: <strong>{(stats.queue_depth ?? 0).toLocaleString()}</strong> jobs
      </span>
      <span className="text-slate-400">|</span>
      <span>
        Workers: <strong>{(stats.active_workers ?? 0).toLocaleString()}</strong>{" "}
        active
      </span>
    </div>
  );
}

/**
 * AdminDashboard — six headline stat cards for the operator.
 *
 * Fetches GET /api/admin/dashboard/stats on mount and re-polls every 60s.
 */
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await getAdminStats();
      setStats(data);
      setError(null);
    } catch {
      setError("Failed to load admin stats.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }
  if (error || !stats) {
    return (
      <div>
        <p className="text-sm text-red-600">{error ?? "No data."}</p>
        <button
          type="button"
          onClick={load}
          className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
        >
          Retry
        </button>
      </div>
    );
  }

  const cards = [
    { label: "Total Orgs", value: (stats.total_orgs ?? 0).toLocaleString() },
    { label: "Total Users", value: (stats.total_users ?? 0).toLocaleString() },
    {
      label: "Active Subscriptions",
      value: (stats.active_subscriptions ?? 0).toLocaleString(),
    },
    { label: "MRR", value: formatCurrency(stats.mrr) },
    { label: "Scans Today", value: (stats.scans_today ?? 0).toLocaleString() },
    {
      label: "New Orgs (30d)",
      value: (stats.new_orgs_last_30_days ?? 0).toLocaleString(),
    },
  ];

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 mb-4">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <StatCard key={c.label} label={c.label} value={c.value} />
        ))}
      </div>
      <CeleryWidget />
    </div>
  );
}

export default AdminDashboard;
